import * as THREE from "three";
import { config } from "../config.js";

// SENSEOFVOID(sawagit/newGame の model-preview/mothership-geometry.js、2026-10-30 時点)からコピーした母艦の形。
// 2026-10-30 オーナー要望「母艦がのっぺりしているので、SENSEOFVOID の主砲口の付いたモデルを持ってくる」。
// 変えた所: 設定の読み先(config.graphics.bossLook)だけ。使うのは pods.js の createBossShip。
// 以下は元の説明:
// 母艦の形(2026-10-16 オーナー要望: 安っぽさを減らす。重さはほとんど増やさない)。見た目だけで、当たり判定は変えない。
// models.jsのcreateMothershipが使う。どの母艦も同じ形なので、形は1回だけ作って使い回す。
// 向き: 艦首=-Z、艦尾=+Z、上=+Y、右=+X(前方はローカルZマイナスで統一)。
//   - 胴体: 横長の六角柱(艦首側の半径20m・艦尾側15m、横だけ2倍)。6本の稜線と前後の縁を斜めに削る(面取り)
//   - 部品(1つの形にまとめる): 船首の主砲の砲口・艦尾の噴射口3つ・胴体を一周する補強の帯・対空砲の台座
//   - 光る所(1つの形): 主砲の砲口の奥・噴射口の奥
//   - 対空砲の砲塔(1つの形を6門ぶん並べる。撃つ向きに回す。mothership-turret-view.js)
// 部品の濃淡は頂点の色で付ける(材質を分けると描く回数が増えるため)

export const HULL_LENGTH = 200;
export const BOW_Z = -HULL_LENGTH / 2;
export const STERN_Z = HULL_LENGTH / 2;
const R_BOW = 20; // 艦首側の半径(縦方向。円柱のときのradiusBottom)
const R_STERN = 15; // 艦尾側の半径(radiusTop)
const X_SCALE = 2; // 横だけ拡大(艦首側の横幅80m)

const look = () => config.graphics.bossLook;

// zの位置の胴体の半径(縦方向)
export function hullRadiusAt(z) {
  const t = (z - BOW_Z) / (STERN_Z - BOW_Z);
  return R_BOW + (R_STERN - R_BOW) * t;
}

// zの位置の、側面(真横の縦の面)の中心からの距離
export function hullFlankXAt(z) {
  return X_SCALE * hullRadiusAt(z) * Math.sin(Math.PI / 3);
}

// 胴体の断面(角を面取りした横長の六角形)。角kは、船底の稜線(k=0)から右回り(右下→右→右上→甲板の稜線→…)。
// u: 胴体の画像の横の位置(今までの円柱と同じく、角k=k/6。textures/mothership-hull-guide.png)。最後に継ぎ目(u=1)を足す
function hullSection(r, chamferRatio) {
  const corners = [];
  for (let k = 0; k < 6; k++) {
    const th = (k * Math.PI) / 3;
    corners.push(new THREE.Vector2(X_SCALE * r * Math.sin(th), -r * Math.cos(th)));
  }
  const pts = [];
  for (let k = 0; k < 6; k++) {
    const a = corners[k];
    const b = corners[(k + 1) % 6];
    const t = Math.min(0.3, (chamferRatio * r) / a.distanceTo(b)); // 角から削る長さ(辺の長さに対する割合)
    if (t > 0) {
      const p0 = a.clone().lerp(b, t);
      const p1 = a.clone().lerp(b, 1 - t);
      pts.push({ x: p0.x, y: p0.y, u: (k + t) / 6 }, { x: p1.x, y: p1.y, u: (k + 1 - t) / 6 });
    } else {
      pts.push({ x: a.x, y: a.y, u: k / 6 });
    }
  }
  pts.push({ ...pts[0], u: 1 });
  return pts;
}

// 三角形を足していく道具。頂点は共有しない(平らな面ごとの法線にして、面取りの角をくっきり見せるため)
class TriangleList {
  constructor() {
    this.position = [];
    this.uv = [];
    this.color = [];
    this.groups = [];
  }
  triangle(a, b, c, uvs = null, color = WHITE) {
    this.position.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
    for (let i = 0; i < 3; i++) {
      this.uv.push(uvs ? uvs[i][0] : 0, uvs ? uvs[i][1] : 0);
      this.color.push(color[0], color[1], color[2]);
    }
  }
  // a→b→c→dの順に外から見て反時計回り
  quad(a, b, c, d, uvs = null, color = WHITE) {
    this.triangle(a, b, c, uvs && [uvs[0], uvs[1], uvs[2]], color);
    this.triangle(a, c, d, uvs && [uvs[0], uvs[2], uvs[3]], color);
  }
  get vertexCount() {
    return this.position.length / 3;
  }
  // ここまでに足した三角形のうち、startより後をmaterialIndexの材質で描く
  group(start, materialIndex) {
    this.groups.push([start, this.vertexCount - start, materialIndex]);
  }
  build() {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(this.position, 3));
    g.setAttribute("uv", new THREE.Float32BufferAttribute(this.uv, 2));
    g.setAttribute("color", new THREE.Float32BufferAttribute(this.color, 3));
    for (const [start, count, m] of this.groups) g.addGroup(start, count, m);
    g.computeVertexNormals();
    return g;
  }
}

// 部品の頂点の色(材質の色に掛ける)
const WHITE = [1, 1, 1];
const MID = [0.55, 0.56, 0.6];
const DARK = [0.18, 0.18, 0.2];
const NOZZLE = [0.35, 0.36, 0.4];

// 胴体。材質のグループ: 0=側面(と面取りの縁)、1=艦尾の端面、2=艦首の端面(今までの円柱と同じ並び)
function buildHull() {
  const { chamferRatio, edgeBevelM } = look();
  const list = new TriangleList();
  // 前後の方向の輪: [z, 内側へ寄せる量(m)]。前後の端は縁を斜めに削る
  const rings = [
    [BOW_Z, edgeBevelM],
    [BOW_Z + edgeBevelM * 1.2, 0],
    [STERN_Z - edgeBevelM * 1.2, 0],
    [STERN_Z, edgeBevelM],
  ];
  const ringPoints = rings.map(([z, inset]) => {
    const r = hullRadiusAt(z);
    const s = (r - inset) / r;
    return hullSection(r, chamferRatio).map((p) => ({ p: new THREE.Vector3(p.x * s, p.y * s, z), u: p.u }));
  });
  // 側面の画像の縦: 上=艦尾(v=1)、下=艦首(v=0)
  const vOf = (z) => (z - BOW_Z) / HULL_LENGTH;
  const sideStart = list.vertexCount;
  for (let i = 0; i < rings.length - 1; i++) {
    const A = ringPoints[i];
    const B = ringPoints[i + 1];
    const va = vOf(rings[i][0]);
    const vb = vOf(rings[i + 1][0]);
    for (let k = 0; k < A.length - 1; k++) {
      list.quad(A[k].p, A[k + 1].p, B[k + 1].p, B[k].p, [
        [A[k].u, va],
        [A[k + 1].u, va],
        [B[k + 1].u, vb],
        [B[k].u, vb],
      ]);
    }
  }
  list.group(sideStart, 0);
  // 前後の端面: 画像を平らに投影して貼る(画像の外枠=端面を囲む長方形)。外から見て左右・上下がそのまま見える向き
  //   艦尾: 後ろから(-Zの向きに)見る → 右=+X。艦首: 前から(+Zの向きに)見る → 右=-X
  const cap = (points, z, materialIndex, facesStern) => {
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const { p } of points) {
      minX = Math.min(minX, p.x);
      maxX = Math.max(maxX, p.x);
      minY = Math.min(minY, p.y);
      maxY = Math.max(maxY, p.y);
    }
    const uvOf = (p) => {
      const u = (p.x - minX) / (maxX - minX);
      return [facesStern ? u : 1 - u, (p.y - minY) / (maxY - minY)];
    };
    const c = new THREE.Vector3(0, 0, z);
    const start = list.vertexCount;
    for (let k = 0; k < points.length - 1; k++) {
      const p = points[k].p;
      const q = points[k + 1].p;
      if (facesStern) list.triangle(c, p, q, [uvOf(c), uvOf(p), uvOf(q)]);
      else list.triangle(c, q, p, [uvOf(c), uvOf(q), uvOf(p)]);
    }
    list.group(start, materialIndex);
  };
  cap(ringPoints[ringPoints.length - 1], STERN_Z, 1, true);
  cap(ringPoints[0], BOW_Z, 2, false);
  return list.build();
}

// 回転体を、段ごとに別々の帯として作る(段の角で法線をなめらかにしないため)。
// profile: [[半径, 高さ], ...](回転の軸=Y)、colorOf(i): i番目の帯の色
function steppedLathe(profile, segments, colorOf) {
  const parts = [];
  for (let i = 0; i < profile.length - 1; i++) {
    const g = new THREE.LatheGeometry(
      [new THREE.Vector2(profile[i][0], profile[i][1]), new THREE.Vector2(profile[i + 1][0], profile[i + 1][1])],
      segments
    );
    parts.push(paint(g, colorOf(i)));
  }
  return mergeGeometries(parts);
}

// 形に、頂点の色(全部同じ色)とuv(なければ0)を付ける。共有していない頂点にそろえる(まとめるため)
function paint(geometry, color) {
  const g = geometry.index ? geometry.toNonIndexed() : geometry;
  const n = g.attributes.position.count;
  const colors = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) colors.set(color, i * 3);
  g.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  if (!g.attributes.uv) g.setAttribute("uv", new THREE.BufferAttribute(new Float32Array(n * 2), 2));
  if (!g.attributes.normal) g.computeVertexNormals();
  return g;
}

// 共有していない頂点の形を1つにまとめる(位置・法線・uv・色)
function mergeGeometries(geometries) {
  const out = new THREE.BufferGeometry();
  for (const name of ["position", "normal", "uv", "color"]) {
    const itemSize = geometries[0].attributes[name].itemSize;
    let total = 0;
    for (const g of geometries) total += g.attributes[name].array.length;
    const array = new Float32Array(total);
    let offset = 0;
    for (const g of geometries) {
      array.set(g.attributes[name].array, offset);
      offset += g.attributes[name].array.length;
    }
    out.setAttribute(name, new THREE.BufferAttribute(array, itemSize));
  }
  return out;
}

// 主砲の砲口: 船首の端面の画像の砲口の位置(真ん中)から、段の付いた砲身を突き出し、根元に冷却フィンを8枚付ける。
// 砲口の先は船首から15.5m前(ビームの出る位置config.combat.mothershipMainGunForwardM=船首から15m前の、すぐ前)
function buildMainGun() {
  // [半径, 船首の端面からの前方距離]。最後の4点は砲口の内側(暗い)
  const profile = [
    [13.5, -0.5], [13.5, 2], [11.5, 3], [11.5, 7.5], [12.8, 8], [12.8, 9.5], [11, 10],
    [11, 13], [12.5, 13.5], [12.5, 15], [10.5, 15.5],
    [8.5, 15.5], [8.5, 8], [5, 7], [0.001, 7],
  ];
  const parts = [steppedLathe(profile, 24, (i) => (i >= 10 ? DARK : i === 4 || i === 8 ? MID : WHITE))];
  for (let k = 0; k < 8; k++) {
    const fin = new THREE.BoxGeometry(2.6, 4.5, 1.2); // x=半径方向、y=砲身の方向、z=厚み
    fin.translate(12.6, 5.2, 0);
    fin.rotateY((k * Math.PI) / 4 + Math.PI / 8);
    parts.push(paint(fin, MID));
  }
  const g = mergeGeometries(parts);
  g.rotateX(-Math.PI / 2); // 回転の軸(Y)を前方(-Z)へ
  g.translate(0, 0, BOW_Z);
  return g;
}

// 艦尾の噴射口(艦尾の端面の画像のエンジン3つの位置。ラッパ形)
export const ENGINE_POSITIONS = [-9.2, 0, 9.2].map((x) => ({ x, y: -4.9 }));
export const ENGINE_LENGTH_M = 5; // 艦尾の端面から後ろへ突き出す長さ
function buildEngines() {
  const profile = [[3.6, -0.5], [3.6, 1], [4.6, 4.5], [4.9, ENGINE_LENGTH_M], [4.1, ENGINE_LENGTH_M], [3.0, 1.2], [0.001, 1.2]];
  const colorOf = (i) => (i >= 3 ? DARK : i === 0 ? MID : NOZZLE);
  return mergeGeometries(
    ENGINE_POSITIONS.map(({ x, y }) => {
      const g = steppedLathe(profile, 20, colorOf);
      g.rotateX(Math.PI / 2); // 回転の軸(Y)を後方(+Z)へ
      g.translate(x, y, STERN_Z);
      return g;
    })
  );
}

// 胴体を一周する補強の帯(断面の外側へ少し張り出した輪)
function buildRibs() {
  const { chamferRatio, ribZs } = look();
  const list = new TriangleList();
  for (const z0 of ribZs) {
    const layers = [[z0 - 2.2, 0], [z0 - 1.6, 0.9], [z0 + 1.6, 0.9], [z0 + 2.2, 0]]; // [z, 外へ張り出す量(m)]
    const rings = layers.map(([z, out]) => {
      const r = hullRadiusAt(z);
      const s = (r + out) / r;
      return hullSection(r, chamferRatio).map((p) => new THREE.Vector3(p.x * s, p.y * s, z));
    });
    for (let i = 0; i < rings.length - 1; i++) {
      for (let k = 0; k < rings[i].length - 1; k++) list.quad(rings[i][k], rings[i][k + 1], rings[i + 1][k + 1], rings[i + 1][k], null, MID);
    }
  }
  const g = list.build();
  g.clearGroups();
  return g;
}

// 対空砲の並び(シミュレーションの砲と同じ前後の位置。fleet-choreography.jsのcreateTurrets)。
// 見た目は胴体の側面に付ける(シミュレーションの砲の位置は中心から左右config.mothershipTurret.offsetXで、側面より少し外)。
// side: -1=左舷 / +1=右舷。並び順はcreateTurretsと同じ(左舷の艦首側から)
export function turretMounts() {
  const { perSide, spacingZ } = config.graphics.bossLook.turrets;
  const mounts = [];
  for (const side of [-1, 1]) {
    for (let k = 0; k < perSide; k++) {
      const z = (k - (perSide - 1) / 2) * spacingZ;
      mounts.push({ side, z, x: side * hullFlankXAt(z) });
    }
  }
  return mounts;
}
export const TURRET_PIVOT_HEIGHT_M = 1.9; // 側面から砲塔の回る中心までの高さ

// 対空砲の台座1つ(上=+Yを外向き。底が原点の少し下)
function turretBaseGeometry() {
  const base = new THREE.CylinderGeometry(3.6, 4.4, 1.8, 8);
  base.translate(0, 0.6, 0);
  return paint(base, MID);
}

// アステロイド基地の入り口の対空砲の台座(2026-10-26。base-turret-view.js): 母艦の台座の下に、岩へ差し込む柱(長さpillarM、-Yの向き)を付けたもの
export function turretPedestalGeometry(pillarM) {
  const pillar = new THREE.CylinderGeometry(2.4, 3.2, pillarM, 8);
  pillar.translate(0, -0.3 - pillarM / 2, 0);
  return mergeGeometries([turretBaseGeometry(), paint(pillar, DARK)]);
}

// 対空砲の台座(胴体に固定。上=+Yを外向きにして作り、側面へ向けて置く)
function buildTurretBases() {
  return mergeGeometries(
    turretMounts().map(({ side, z, x }) => {
      const g = turretBaseGeometry();
      g.rotateZ(-side * (Math.PI / 2)); // +Yを外向き(±X)へ
      g.translate(x, 0, z);
      return g;
    })
  );
}

// 対空砲の砲塔(丸い砲塔と砲身2本)。回る中心を原点に、砲身は+Yの向き(何もしなければ側面の真外を向く)
function buildTurretHead() {
  const ball = paint(new THREE.SphereGeometry(2.6, 12, 8), WHITE);
  const parts = [ball];
  for (const dx of [-0.85, 0.85]) {
    const barrel = new THREE.BoxGeometry(0.6, 6, 0.6);
    barrel.translate(dx, 4.5, 0);
    parts.push(paint(barrel, DARK));
  }
  return mergeGeometries(parts);
}

// 光る所(主砲の砲口の奥・噴射口の奥)。材質は光る単色
function buildGlows() {
  const parts = [];
  const gun = new THREE.CircleGeometry(4.8, 24);
  gun.rotateY(Math.PI); // 前(-Z)を向ける
  gun.translate(0, 0, BOW_Z - 7.2);
  parts.push(paint(gun, WHITE));
  for (const { x, y } of ENGINE_POSITIONS) {
    const g = new THREE.CircleGeometry(2.8, 16);
    g.translate(x, y, STERN_Z + 1.3);
    parts.push(paint(g, WHITE));
  }
  return mergeGeometries(parts);
}

let shapes = null;
// 母艦の形(全部の母艦で共有する。捨てない)
export function mothershipShapes() {
  if (!shapes) {
    shapes = {
      hull: buildHull(),
      parts: mergeGeometries([buildMainGun(), buildEngines(), buildRibs(), buildTurretBases()]),
      glows: buildGlows(),
      turretHead: buildTurretHead(),
    };
  }
  return shapes;
}
